import { useRef, useState } from "react";

import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { ProgressSpinner } from "primereact/progressspinner";

import { C, themeClasses } from "sj-ui-toolkit/src/atlas/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CompanyLogoCardProps {
  uploadLogo: (file: File) => Promise<void>;
  removeLogo: () => Promise<void>;
  showToast?: (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
    detail: string,
    life?: number
  ) => void;
  thumbLogoUrl?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CompanyLogoCard = ({
  uploadLogo,
  removeLogo,
  showToast = () => {},
  thumbLogoUrl,
}: CompanyLogoCardProps) => {
  const fileUploadRef = useRef<FileUpload>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleUpload = async (e: { files: File[] }) => {
    const file = e.files?.[0];
    if (file) {
      setIsUploading(true);
      try {
        await uploadLogo(file);
        showToast("success", "Success", "Logo uploaded successfully");
      } catch {
        showToast("error", "Error", "Failed to upload logo");
      } finally {
        setIsUploading(false);
        fileUploadRef.current?.clear();
      }
    }
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      await removeLogo();
      showToast("success", "Success", "Logo removed successfully");
    } catch {
      showToast("error", "Error", "Failed to remove logo");
    } finally {
      setIsRemoving(false);
    }
  };

  const hasLogo = thumbLogoUrl && thumbLogoUrl !== "loading";
  const isLoading = isUploading || isRemoving;

  const chooseOptions = {
    icon: "pi pi-pencil",
    iconOnly: true,
    className: "p-button-primary p-button-rounded p-1",
  };

  return (
    <div
      className={C(themeClasses.bgElevation2, "p-6 rounded-md shadow-md")}
      data-testid="company-logo-card"
    >
      <div
        className={C(
          "flex justify-between items-center border-b-2 pb-3 mb-6",
          themeClasses.borderSurfaceBorder
        )}
      >
        <h3 className="text-xl font-semibold flex-1 mb-0">Company Logo</h3>
      </div>
      <div className="flex flex-col gap-6">
        <p className="atlas-h5">Edit your company logo</p>
        <div className="flex items-start gap-8">
          <div className="relative inline-block">
            <div
              className={C(
                "w-20 h-20 border-2 border-surface-400 rounded flex items-center justify-center overflow-hidden",
                themeClasses.bgElevation1
              )}
            >
              {isLoading ? (
                <ProgressSpinner style={{ width: "30px", height: "30px" }} />
              ) : hasLogo ? (
                <img
                  src={thumbLogoUrl}
                  alt="Company logo"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className={C("w-full h-full", themeClasses.bgElevation3)} />
              )}
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
              <FileUpload
                ref={fileUploadRef}
                mode="basic"
                accept="image/*"
                auto
                customUpload
                uploadHandler={handleUpload}
                chooseOptions={chooseOptions}
                disabled={isLoading}
                pt={{
                  chooseButton: {
                    root: { className: "w-10 h-10 rounded-full shadow-md" },
                  },
                }}
              />
            </div>
          </div>
          {hasLogo && (
            <div className="flex flex-col gap-3 pt-2">
              <Button
                label="Remove Logo"
                icon="pi pi-trash"
                severity="danger"
                outlined
                size="small"
                data-testid="company-logo-remove-button"
                onClick={handleRemove}
                disabled={isLoading}
                className="mt-2"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
